/**
 * MyPodOps Studio V2 - Multi-Participant Session Manager
 *
 * Handles:
 * - VideoSDK.live WebRTC integration for real-time communication
 * - Local recording per participant (high-quality separate tracks)
 * - Guest management and invitations
 * - Green room and waiting room
 * - Screen sharing and chat
 */

import {
  SessionRecorder,
  RecorderOptions,
  getMediaConstraints,
  getScreenConstraints,
  RecordingTrack
} from './recorder';
import { StudioUploader, createUploadSession, SessionMetadata } from './uploader';
import { sessionsApi, invitesApi } from './api';
import { videoSDK, VideoSDKConfig, VideoSDKEvents } from './videosdk';

// Simple ID generator for local use (invite tokens generated server-side)
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export interface Participant {
  id: string;
  name: string;
  email?: string;
  role: 'host' | 'guest' | 'producer';
  status: 'invited' | 'waiting' | 'joined' | 'left';
  tracks: {
    video?: MediaStreamTrack;
    audio?: MediaStreamTrack;
    screen?: MediaStreamTrack;
  };
  isLocal: boolean;
  isMuted: boolean;
  isVideoOff: boolean;
  joinedAt?: number;
}

export interface SessionConfig {
  title: string;
  hostId: string;
  hostName: string;
  quality: '4K' | '1080p' | '720p' | 'audio-only';
  enableChat: boolean;
  enableGreenRoom: boolean;
  enableRecording: boolean;
  maxParticipants: number;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: number;
}

export type SessionState = 'idle' | 'green-room' | 'waiting' | 'live' | 'recording' | 'ended';

export interface SessionEvents {
  onParticipantJoined?: (participant: Participant) => void;
  onParticipantLeft?: (participantId: string) => void;
  onParticipantUpdated?: (participant: Participant) => void;
  onChatMessage?: (message: ChatMessage) => void;
  onStateChange?: (state: SessionState) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: (tracks: RecordingTrack[]) => void;
  onError?: (error: Error) => void;
}

/**
 * StudioSession - Main session controller
 */
export class StudioSession {
  private sessionId: string;
  private meetingId: string | null = null;
  private config: SessionConfig;
  private events: SessionEvents;
  private participants: Map<string, Participant> = new Map();
  private localStream: MediaStream | null = null;
  private screenStream: MediaStream | null = null;
  private state: SessionState = 'idle';
  private recorder: SessionRecorder | null = null;
  private uploader: StudioUploader | null = null;
  private token: string;
  private chatMessages: ChatMessage[] = [];
  private videoSDKToken: string | null = null;

  constructor(config: SessionConfig, token: string, events: SessionEvents = {}) {
    this.sessionId = generateId();
    this.config = config;
    this.token = token;
    this.events = events;

    console.log(`[Session] Created: ${this.sessionId}`);
  }

  /**
   * Initialize the session and get media permissions (for HOST)
   */
  async initialize(): Promise<void> {
    try {
      // Get local media stream
      const constraints = getMediaConstraints(this.config.quality);
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Create local participant
      const localParticipant: Participant = {
        id: this.config.hostId,
        name: this.config.hostName,
        role: 'host',
        status: 'joined',
        tracks: {
          video: this.localStream.getVideoTracks()[0],
          audio: this.localStream.getAudioTracks()[0]
        },
        isLocal: true,
        isMuted: false,
        isVideoOff: false,
        joinedAt: Date.now()
      };

      this.participants.set(localParticipant.id, localParticipant);

      // Create meeting room via VideoSDK API
      await this.createMeetingRoom();

      // Join the VideoSDK meeting
      await this.joinVideoSDKMeeting();

      this.setState('green-room');
      console.log('[Session] Initialized successfully');
    } catch (error) {
      console.error('[Session] Initialize failed:', error);
      this.events.onError?.(error as Error);
      throw error;
    }
  }

  // Unique participant ID for this instance (generated once, used for guests to avoid conflicts)
  private localParticipantId: string | null = null;

  /**
   * Initialize as guest - join an existing meeting (for GUEST)
   * Guest receives meetingId and videoSDKToken via invite link
   */
  async initializeAsGuest(meetingId: string, videoSDKToken: string): Promise<void> {
    try {
      console.log('[Session] Initializing as guest for meeting:', meetingId);

      // Generate a UNIQUE participantId for this guest to avoid conflicts with host
      // This is critical - VideoSDK kicks duplicate participantIds (error 4005)
      this.localParticipantId = `guest-${generateId()}`;
      console.log('[Session] Generated unique guest participantId:', this.localParticipantId);

      // Get local media stream
      const constraints = getMediaConstraints(this.config.quality);
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Create local participant as guest with UNIQUE ID
      const localParticipant: Participant = {
        id: this.localParticipantId,
        name: this.config.hostName,
        role: 'guest',
        status: 'joined',
        tracks: {
          video: this.localStream.getVideoTracks()[0],
          audio: this.localStream.getAudioTracks()[0]
        },
        isLocal: true,
        isMuted: false,
        isVideoOff: false,
        joinedAt: Date.now()
      };

      this.participants.set(localParticipant.id, localParticipant);

      // Set the meeting ID and VideoSDK token directly (don't create new meeting)
      this.meetingId = meetingId;
      this.videoSDKToken = videoSDKToken;

      // Join the existing VideoSDK meeting
      await this.joinVideoSDKMeeting();

      this.setState('green-room');
      console.log('[Session] Initialized as guest successfully');
    } catch (error) {
      console.error('[Session] Guest initialization failed:', error);
      this.events.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Create VideoSDK meeting room via API
   */
  private async createMeetingRoom(): Promise<void> {
    try {
      const session = await sessionsApi.create({
        hostId: this.config.hostId,
        hostName: this.config.hostName,
        sessionName: this.config.title,
        settings: {
          waitingRoom: this.config.enableGreenRoom,
          maxParticipants: this.config.maxParticipants,
          recordingQuality: this.mapQualityToApi(this.config.quality),
          allowScreenShare: true,
          allowChat: this.config.enableChat,
        }
      });

      this.sessionId = session.sessionId;
      this.meetingId = session.videoSDKRoomId;
      this.videoSDKToken = session.videoSDKToken;

      console.log(`[Session] Meeting created: ${this.meetingId}`);
    } catch (error) {
      console.error('[Session] Failed to create meeting:', error);
      throw new Error(`Failed to create meeting: ${(error as Error).message}`);
    }
  }

  /**
   * Map quality preset to API format
   */
  private mapQualityToApi(quality: string): '4k' | '1080p' | '720p' | 'audio_only' {
    const map: Record<string, '4k' | '1080p' | '720p' | 'audio_only'> = {
      '4K': '4k',
      '1080p': '1080p',
      '720p': '720p',
      'audio-only': 'audio_only'
    };
    return map[quality] || '1080p';
  }

  /**
   * Join the VideoSDK meeting after room creation
   */
  private async joinVideoSDKMeeting(): Promise<void> {
    if (!this.meetingId || !this.videoSDKToken) {
      throw new Error('Meeting not created yet');
    }

    // Use unique localParticipantId for guests, otherwise use hostId for hosts
    const participantId = this.localParticipantId || this.config.hostId;
    console.log('[Session] Joining VideoSDK with participantId:', participantId);

    const videoSDKConfig: VideoSDKConfig = {
      meetingId: this.meetingId,
      token: this.videoSDKToken,
      participantId: participantId,
      name: this.config.hostName,
      micEnabled: true,
      webcamEnabled: this.config.quality !== 'audio-only',
      maxResolution: this.mapQualityToVideoSDK(this.config.quality)
    };

    const videoSDKEvents: VideoSDKEvents = {
      onMeetingJoined: () => {
        console.log('[Session] VideoSDK meeting joined');
      },
      onMeetingLeft: () => {
        console.log('[Session] VideoSDK meeting left');
      },
      onParticipantJoined: (participant) => {
        const newParticipant: Participant = videoSDK.toParticipant(participant);
        this.participants.set(participant.id, newParticipant);
        this.events.onParticipantJoined?.(newParticipant);
      },
      onParticipantLeft: (participantId) => {
        this.participants.delete(participantId);
        this.events.onParticipantLeft?.(participantId);
      },
      onChatMessage: (msg) => {
        const chatMessage: ChatMessage = {
          id: generateId(),
          senderId: msg.senderId,
          senderName: msg.senderName,
          message: msg.message,
          timestamp: msg.timestamp
        };
        this.chatMessages.push(chatMessage);
        this.events.onChatMessage?.(chatMessage);
      },
      onError: (error) => {
        this.events.onError?.(error);
      }
    };

    await videoSDK.joinMeeting(videoSDKConfig, videoSDKEvents);
  }

  /**
   * Map quality preset to VideoSDK resolution
   */
  private mapQualityToVideoSDK(quality: string): '4k' | '1080p' | '720p' | '480p' {
    const map: Record<string, '4k' | '1080p' | '720p' | '480p'> = {
      '4K': '4k',
      '1080p': '1080p',
      '720p': '720p',
      'audio-only': '480p'
    };
    return map[quality] || '1080p';
  }

  /**
   * Generate guest invite link
   */
  async generateInviteLink(guestName?: string): Promise<string> {
    // Generate invite link client-side (backend /studio/invites/generate endpoint doesn't exist)
    // GreenRoom.tsx parses: session, meeting, token (required for VideoSDK), name (optional)
    const baseUrl = `${window.location.origin}/studio-v2/join`;
    const params = new URLSearchParams();

    params.set('session', this.sessionId);

    if (this.meetingId) {
      params.set('meeting', this.meetingId);
    }

    // Include VideoSDK token so guests can join the meeting
    if (this.videoSDKToken) {
      params.set('token', this.videoSDKToken);
    }

    if (guestName) {
      params.set('name', guestName);
    }

    const inviteUrl = `${baseUrl}?${params.toString()}`;
    console.log('[Session] Generated invite link:', inviteUrl);

    return inviteUrl;
  }

  /**
   * Send email invite to guest
   */
  async sendEmailInvite(email: string, guestName: string): Promise<void> {
    try {
      await invitesApi.sendEmail({
        sessionId: this.sessionId,
        hostId: this.config.hostId,
        hostName: this.config.hostName,
        sessionName: this.config.title,
        guestEmail: email,
        guestName,
        scheduledTime: new Date().toISOString()
      });

      console.log(`[Session] Invite sent to: ${email}`);
    } catch (error) {
      console.error('[Session] Failed to send email invite:', error);
      throw new Error(`Failed to send email invite: ${(error as Error).message}`);
    }
  }

  /**
   * Start the live session (move from green room to live)
   */
  async goLive(): Promise<void> {
    if (this.state !== 'green-room') {
      throw new Error('Must be in green room to go live');
    }

    this.setState('live');
    console.log('[Session] Now LIVE');
  }

  /**
   * Start recording all participants
   */
  async startRecording(): Promise<void> {
    if (this.state !== 'live') {
      throw new Error('Must be live to start recording');
    }

    // Initialize recorder
    const recorderOptions: RecorderOptions = {
      quality: this.config.quality,
      chunkInterval: 5000, // 5 second chunks for progressive upload
      onDataAvailable: (track, chunk) => {
        // Handle progressive upload
        this.uploader?.handleChunk(track, chunk);
      },
      onStop: async (track, blob) => {
        // Upload final track
        if (this.uploader) {
          await this.uploader.finalizeTrack(track, blob);
        }
      },
      onError: (error) => {
        this.events.onError?.(error);
      }
    };

    this.recorder = new SessionRecorder(this.sessionId, recorderOptions);

    // Initialize uploader
    const metadata: SessionMetadata = {
      title: this.config.title,
      participants: Array.from(this.participants.values()).map(p => p.name),
      quality: this.config.quality
    };

    this.uploader = createUploadSession(
      this.config.hostId,
      this.token,
      metadata,
      (progress) => {
        console.log('[Session] Upload progress:', progress);
      }
    );

    // Add tracks for all participants
    this.participants.forEach(participant => {
      if (participant.tracks.video || participant.tracks.audio) {
        const stream = new MediaStream();
        if (participant.tracks.video) stream.addTrack(participant.tracks.video);
        if (participant.tracks.audio) stream.addTrack(participant.tracks.audio);

        const trackType = participant.tracks.video ? 'video' : 'audio';
        const trackId = this.recorder!.addTrack(stream, participant.id, participant.name, trackType);

        // Register with uploader
        const track = this.recorder!.getTracks().find(t => t.id === trackId);
        if (track) {
          this.uploader?.registerTrack(track);
        }
      }
    });

    // Start recording all tracks
    this.recorder.startAll();
    this.setState('recording');
    this.events.onRecordingStart?.();

    console.log('[Session] Recording started');
  }

  /**
   * Stop recording
   */
  async stopRecording(): Promise<void> {
    if (!this.recorder) {
      throw new Error('Not recording');
    }

    this.recorder.stopAll();
    this.setState('live');

    // Save session metadata
    await this.uploader?.saveSession();

    const tracks = this.recorder.getTracks();
    this.events.onRecordingStop?.(tracks);

    console.log('[Session] Recording stopped');
  }

  /**
   * Get the local participant ID (unique ID for guests, hostId for hosts)
   */
  private getLocalParticipantId(): string {
    return this.localParticipantId || this.config.hostId;
  }

  /**
   * Start screen sharing
   */
  async startScreenShare(): Promise<void> {
    try {
      const constraints = getScreenConstraints();
      this.screenStream = await navigator.mediaDevices.getDisplayMedia(constraints);

      // Update local participant
      const local = this.participants.get(this.getLocalParticipantId());
      if (local) {
        local.tracks.screen = this.screenStream.getVideoTracks()[0];
        this.participants.set(local.id, local);
        this.events.onParticipantUpdated?.(local);
      }

      // Add to recording if active
      if (this.recorder && this.state === 'recording') {
        this.recorder.addTrack(this.screenStream, this.getLocalParticipantId(), this.config.hostName, 'screen');
      }

      // Handle screen share end
      this.screenStream.getVideoTracks()[0].onended = () => {
        this.stopScreenShare();
      };

      console.log('[Session] Screen sharing started');
    } catch (error) {
      console.error('[Session] Screen share failed:', error);
      throw error;
    }
  }

  /**
   * Stop screen sharing
   */
  stopScreenShare(): void {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop());
      this.screenStream = null;

      // Update local participant
      const local = this.participants.get(this.getLocalParticipantId());
      if (local) {
        delete local.tracks.screen;
        this.participants.set(local.id, local);
        this.events.onParticipantUpdated?.(local);
      }

      console.log('[Session] Screen sharing stopped');
    }
  }

  /**
   * Toggle mute for local participant
   * Uses VideoSDK's native methods to properly enable/disable mic
   */
  toggleMute(): boolean {
    const local = this.participants.get(this.getLocalParticipantId());
    if (!local) return false;

    // Use VideoSDK's native toggle method
    // toggleMic returns true if mic is ON, false if OFF
    const micOn = videoSDK.toggleMic();
    local.isMuted = !micOn;

    // Also update the track if we have a reference
    if (local.tracks.audio) {
      local.tracks.audio.enabled = micOn;
    }

    this.participants.set(local.id, local);
    this.events.onParticipantUpdated?.(local);
    console.log('[Session] Mic toggled, muted:', local.isMuted);
    return local.isMuted;
  }

  /**
   * Toggle video for local participant
   * Uses VideoSDK's native methods to properly enable/disable webcam
   */
  toggleVideo(): boolean {
    const local = this.participants.get(this.getLocalParticipantId());
    if (!local) return false;

    // Use VideoSDK's native toggle method
    // toggleWebcam returns true if webcam is ON, false if OFF
    const webcamOn = videoSDK.toggleWebcam();
    local.isVideoOff = !webcamOn;

    // Also update the track if we have a reference
    if (local.tracks.video) {
      local.tracks.video.enabled = webcamOn;
    }

    this.participants.set(local.id, local);
    this.events.onParticipantUpdated?.(local);
    console.log('[Session] Webcam toggled, video off:', local.isVideoOff);
    return local.isVideoOff;
  }

  /**
   * Send chat message
   */
  sendMessage(message: string): void {
    const local = this.participants.get(this.getLocalParticipantId());
    if (!local) return;

    const chatMessage: ChatMessage = {
      id: generateId(),
      senderId: local.id,
      senderName: local.name,
      message,
      timestamp: Date.now()
    };

    this.chatMessages.push(chatMessage);
    this.events.onChatMessage?.(chatMessage);

    // Broadcast via VideoSDK to other participants
    videoSDK.sendMessage(message);
  }

  /**
   * End the session
   */
  async endSession(): Promise<void> {
    // Stop recording if active
    if (this.state === 'recording') {
      await this.stopRecording();
    }

    // Stop screen share
    this.stopScreenShare();

    // Stop local stream
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    // Leave VideoSDK meeting
    await videoSDK.leaveMeeting();

    this.setState('ended');
    console.log('[Session] Ended');
  }

  /**
   * Get current state
   */
  getState(): SessionState {
    return this.state;
  }

  /**
   * Get all participants
   */
  getParticipants(): Participant[] {
    return Array.from(this.participants.values());
  }

  /**
   * Get local stream
   */
  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Get screen stream
   */
  getScreenStream(): MediaStream | null {
    return this.screenStream;
  }

  /**
   * Get session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get meeting ID
   */
  getMeetingId(): string | null {
    return this.meetingId;
  }

  /**
   * Get chat messages
   */
  getChatMessages(): ChatMessage[] {
    return this.chatMessages;
  }

  /**
   * Get session config
   */
  getConfig(): SessionConfig {
    return this.config;
  }

  /**
   * Set state and notify
   */
  private setState(state: SessionState): void {
    this.state = state;
    this.events.onStateChange?.(state);
  }
}

/**
 * Create a new studio session
 */
export function createStudioSession(
  config: SessionConfig,
  token: string,
  events?: SessionEvents
): StudioSession {
  return new StudioSession(config, token, events);
}
